module Meevo.Plugins.MeevoMessenger {

    import IMeevoMessengerNotification = Meevo.Model.IMeevoMessengerNotification;
    import Employee = Meevo.Model.Employee;
    import ClientListItem = Meevo.Model.ClientListItem;
    import BusinessHours = Meevo.Model.BusinessHours;
    import WeekdayNameEnum = Meevo.Model.WeekdayNameEnum;
    import PresetStatus = Meevo.Components.Controls.PresetStatus;

    export interface IMeevoMessengerService {
        getFilterSettingsByFilterOption: (filterSettings: IConversationBodyRequest, isWithoutUnknownLogic?: boolean) => IConversationBodyRequest;
        getFilterSettingsByMessageStatus: (value: MessageFilter) => { isFollowUp: boolean, isNewMessage: boolean };
        getUserName: (selectedConversation: MeevoMessengerConversation, message?: IMeevoMessengerNotification | MeevoMessengerMessage) => string;
        isTurnOnTogglesPulsing: (existingConversations: MeevoMessengerConversation[], conversationsWithInboundMessages: any) => boolean;

        preparedMessagesList: (selectedConversation: MeevoMessengerConversation, messages: MeevoMessengerMessage[], timeZoneOffset: string, samplesForTimeLineChecking: MeevoMessengerMessage[], onAttachmentsGet?: Function) => Array<IMeevoMessengerNotification | MeevoMessengerMessage>;
        markDifferentDays: (messagesList: Array<IMeevoMessengerNotification | MeevoMessengerMessage>, timeZoneOffset: string, isPartialList?: boolean) => Array<IMeevoMessengerNotification | MeevoMessengerMessage>;
        getOptionsForSystemAutoReply: () => ng.IPromise<ISystemAutoReply>;
        isMessageDuplicate: (messageList: MeevoMessengerMessage[], newMessageId: Guid) => boolean;
        getSplittedMessagesList: (msg: string, msgLimit?: number) => Array<string>;
        getAttachmentsForMessage: (messages: MeevoMessengerMessage, onAttachmentGet?: Function) => MeevoMessengerMessage | IMeevoMessengerNotification;
        findConversation: (conversationsList: Array<MeevoMessengerConversation>, properties?: any) => MeevoMessengerConversation;
        getBusinessHoursForDay: (dayNumber: number, businessHoursList: BusinessHours[]) => BusinessHours;
        calculateBusinessHoursDelay: (startTime: moment.Moment, endTime: moment.Moment, locationTime: string, isNextHoliday: boolean) => ISystemAutoReply;
        convertBusinessHoursToRightFormat: (date: string, to: string) => moment.Moment;
        markMessagesAsRead: (messages: MeevoMessengerMessage[], classification: string) => ng.IPromise<any>;
        getDuplicatedUsers: (filteringOption: string, isGetClients: boolean) => ng.IPromise<IUnifiedDuplicatedUser[]>;
        getStateParamsForClientRouting: (conversation: MeevoMessengerConversation, recipientRoutParams: IRecipientRouteParams, callback?: Function, customClientId?: Guid) => any;
        getClientInfoForSideNav: (clientId: Guid) => ng.IPromise<Meevo.Model.Client>;
        filterConversationList: (checkInFilter:boolean, checkOutFilter: boolean, initialList: Array<MeevoMessengerConversation>) => Array<MeevoMessengerConversation>;
        messengerDateFilter: IDateFilter[];
        messengerMessageFilters: MessageFilter[];
    }

    export class MeevoMessengerService implements IMeevoMessengerService {
        private businessHoursList: BusinessHours[];

        static $inject = [
            'meevo.components.busy.BusyService',
            'meevo.components.common.IdentityService',
            'meevo.plugins.meevoMessenger.meevoMessengerApiService',
            '$translate',
            '$state'
        ];

        constructor(
            protected busyService: Meevo.Components.Busy.IBusyService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected meevoMessengerApiService: IMeevoMessengerApiService,
            protected $translate: angular.translate.ITranslateService,
            protected $state: any
        ) { }

        /**
         * Get filter settings by filter option
         * @param {IConversationBodyRequest} filterSettings
         * @param {boolean} isWithoutUnknownLogic
         * @returns {IConversationBodyRequest}
         */
        getFilterSettingsByFilterOption(filterSettings: IConversationBodyRequest, isWithoutUnknownLogic: boolean): IConversationBodyRequest {
            const unknown = Meevo.Plugins.MeevoMessenger.Constants.DEFAULT_USER_NAME.toLowerCase();
            const settings: IConversationBodyRequest = angular.copy(filterSettings);
            const filterValue = filterSettings.criteria.filter.replace(/([()-])/g , '');
            // Todo: try to refactor this
            // Check if there something in filter
            if (!filterValue.length) {
                settings.criteria.filter = null;
                settings.filterFullName = null;
                settings.filterPhoneNumber = null;
            } else {
                // Check if user print unknown
                if (!isWithoutUnknownLogic && unknown.indexOf(filterValue.toLowerCase()) !== -1) {
                    settings.filterFullName = '';
                    settings.filterPhoneNumber = null;
                } else if (isNaN(+filterValue)) {
                    // If user print characters
                    settings.filterFullName = filterValue;
                    settings.filterPhoneNumber = null;
                } else {
                    // If user print digits
                    settings.filterFullName = null;
                    settings.filterPhoneNumber = null;
                    settings.filterPhoneNumber = filterValue;
                }
            }

            return settings;
        }

        /**
         * Get filter settings by message Status
         * @param {MessageFilter} value
         * @returns {{ isFollowUp: boolean, isNewMessage: boolean }}
         */
        getFilterSettingsByMessageStatus(value: MessageFilter): { isFollowUp: boolean, isNewMessage: boolean } {
            let filterSettingsByMessageStatus: any = {};

            switch (value.type) {
                case MessageFilterOption.All:
                    filterSettingsByMessageStatus.isFollowUp = null;
                    filterSettingsByMessageStatus.isNewMessage = null;
                    filterSettingsByMessageStatus.isLastInbound = null;
                    break;
                case MessageFilterOption.Marked:
                    filterSettingsByMessageStatus.isFollowUp = true;
                    filterSettingsByMessageStatus.isNewMessage = null;
                    break;
                case MessageFilterOption.Unread:
                    filterSettingsByMessageStatus.isLastInbound = true;
                    filterSettingsByMessageStatus.isNewMessage = true;
                    filterSettingsByMessageStatus.isFollowUp = null;
                    break;
                default: break;
            }

            return filterSettingsByMessageStatus;
        }

        /**
         * Get and prepare duplicated user for modal
         * @param {string} filteringOption
         * @param {boolean} isGetClients
         * @returns {ng.IPromise<IUnifiedDuplicatedUser[]>} promise
         */
        getDuplicatedUsers(filteringOption: string, isGetClients: boolean): ng.IPromise<IUnifiedDuplicatedUser[]> {
            this.busyService.start()

            const duplicatedUsersPromise: ng.IPromise<ClientListItem[] | Employee> = isGetClients ?
                this.meevoMessengerApiService.getClientsList(filteringOption) :
                this.meevoMessengerApiService.getEmployeesList(filteringOption);

            return duplicatedUsersPromise.then((duplicatedUsers: ClientListItem[] | Employee[]) => {
                const unifiedData: IUnifiedDuplicatedUser[] = _.map(duplicatedUsers, (duplicatedUser: ClientListItem | Employee) => {
                    return {
                        id: duplicatedUser.id,
                        displayName: duplicatedUser.displayName
                    };
                });

                return unifiedData;
            }).finally(() => this.busyService.stop());
        }

        /**
         * Filter the messages and mark unread messages as read
         * @param {Array<MeevoMessengerMessage>} messages
         * @returns {ng.IPromise<any>} promise
         */
        markMessagesAsRead(messages: MeevoMessengerMessage[], classification: string): ng.IPromise<any> {
            const unreadMessages = messages.filter((message: MeevoMessengerMessage) => !message.isRead && message.isInbound);

            if (!unreadMessages || !unreadMessages.length) return;

            return this.meevoMessengerApiService.externalMarkMessagesAsRead(unreadMessages.map((message: MeevoMessengerMessage) => message.id), classification).then(() => {
                unreadMessages.forEach((message: MeevoMessengerMessage) => message.isRead = true);
            });
        }

        /**
         * Prepared new messages. Add User name for the new messages and set time lines
         * @param {MeevoMessengerConversation} selectedConversation
         * @param {MeevoMessengerMessage[]} messages
         * @param {string} timeZoneOffset
         * @param {MeevoMessengerMessage[]} samplesForTimeLineChecking
         * @param {Function} onAttachmentsGet
         * @returns {Array<IMeevoMessengerNotification | MeevoMessengerMessage>}
         */
        preparedMessagesList(selectedConversation: MeevoMessengerConversation, messages: MeevoMessengerMessage[], timeZoneOffset: string, samplesForTimeLineChecking: MeevoMessengerMessage[], onAttachmentsGet?: Function): Array<IMeevoMessengerNotification | MeevoMessengerMessage> {
            const preparedMessages = messages
                .map((msg: MeevoMessengerMessage) => this.getAttachmentsForMessage(msg, onAttachmentsGet))
                .map((msg: MeevoMessengerMessage) => ({ ...msg, ...{ senderUserName: this.getUserName(selectedConversation, msg) } }))
                .reverse();

            return this.markDifferentDays( [
                ...preparedMessages,
                ...samplesForTimeLineChecking // Use for verification for adding time line
            ], timeZoneOffset);
        }

        /**
         * Prepared messages from endpoint
         * @param {Array<IMeevoMessengerNotification | MeevoMessengerMessage>} messagesList
         * @param {string} timeZoneOffset
         * @param {boolean = false} isPartialList
         * @returns {Array<IMeevoMessengerNotification | MeevoMessengerMessage>}
         */
        markDifferentDays(messagesList: Array<MeevoMessengerMessage>, timeZoneOffset: string, isPartialList: boolean = false): Array<MeevoMessengerMessage> {
            const result = messagesList.slice();
            const offsetNumber = Number(timeZoneOffset.split(':')[0]);

            for (let i = 0; i < result.length; i++) {
                // Fix for the first element in the list
                if (i === 0) {
                    // Check if a list is partial
                    // Check if isShowTimeLine is installed earlier. It's used for partial list
                    if (!isPartialList && typeof result[i].isShowTimeLine === 'undefined') result[i].isShowTimeLine = true;
                    continue;
                }

                let prevMessageDate = moment(result[i - 1].createdDateTime).add(offsetNumber, 'hours')
                let currentMessageDate = moment(result[i].createdDateTime).add(offsetNumber, 'hours');
                const isSameDay = moment(prevMessageDate.format()).isSame(currentMessageDate.format(), 'day');
                result[i].isShowTimeLine = !isSameDay;
            }

            return result;
        }

        /**
         * Check if turn off toggles pulsing after reduce badge number
         * @param {MeevoMessengerConversation[]} existingConversations
         * @param {IMeevoMessengerNewConversations} conversationsWithInboundMessages
         * @returns {boolean}
         */
        isTurnOnTogglesPulsing(existingConversations: MeevoMessengerConversation[], conversationsWithInboundMessages: any): boolean {

            if (!conversationsWithInboundMessages || !Object.keys(conversationsWithInboundMessages).length) return false;

            const conversationsIdsWithInbound: string[] = Object.keys(conversationsWithInboundMessages);
            const existingConversationsIds = _.chain(existingConversations)
                //.filter((conversation: MeevoMessengerConversation) => conversation.lastMessageIsInbound && !conversation.clientId && !conversation.employeeId)
                .pluck('conversationId')
                .value();
            const intersectingIds = _.intersection(conversationsIdsWithInbound, existingConversationsIds);
            const existingUnknownHasInbound: MeevoMessengerConversation[] = _.chain(existingConversations)
                .filter((conversation: MeevoMessengerConversation) => _.contains(intersectingIds, conversation.id))
                .filter((conversation: MeevoMessengerConversation) => !conversation.clientId && !conversation.employeeId)
                .value();

            return existingUnknownHasInbound.length > 0 || intersectingIds.length !== conversationsIdsWithInbound.length;
        }

        /**
         * Get user name for conversations according to input params         
         * @param {MeevoMessengerConversation} selectedConversation
         * @param {MeevoMessengerMessage} message
         * @returns {string}
         */
        getUserName(selectedConversation: MeevoMessengerConversation, message?: IMeevoMessengerNotification | MeevoMessengerMessage): string {
            let name = Meevo.Plugins.MeevoMessenger.Constants.DEFAULT_USER_NAME;

            const { displayName } = selectedConversation;

            if (message && !message.isInbound) {
                name = (<MeevoMessengerMessage>message).senderUserName || this.identityService.current().displayName;
            } else if (displayName) name = `${displayName}`;

            return name;
        }


        /*
         * Main function for getting information for system auto-reply 
         * @returns {ISystemAutoReply | ng.IPromise<ISystemAutoReply} promise
         */
        getOptionsForSystemAutoReply(): ng.IPromise<ISystemAutoReply> {
            let locationTime: string = '';

            return this.meevoMessengerApiService.getLocationTime()
                .then(currentLocationTime => {
                    locationTime = currentLocationTime;
                    return this.meevoMessengerApiService.checkHolidays(locationTime);
                }).then((holidaysStatus: IHolidaysStatusForCurrentDays) => {

                    if (holidaysStatus.isCurrentDayHoliday) {
                        const delay = moment(locationTime).startOf('day').add(1, 'day').diff(moment(locationTime));
                        return { status: OperationModeEnum.Holiday, delay: delay };
                    } else {
                        return this.meevoMessengerApiService.getBusinessHours()
                            .then((businessHoursList: BusinessHours[]) => {
                                return this.checkHours(locationTime, holidaysStatus.isNextDayHoliday, businessHoursList);
                            });
                    }
                });
        }

        /**
         * Check business hours and return delay for the next try
         * @param {string} locationTime
         * @param {boolean} isNextDayHoliday
         * @param {BusinessHours[]} businessHoursList
         * @returns {ISystemAutoReply}
         */
        checkHours(locationTime: string, isNextDayHoliday: boolean, businessHoursList: BusinessHours[]): ISystemAutoReply {
            this.businessHoursList = businessHoursList;

            let currentDay: BusinessHours = this.getBusinessHoursForDay(moment(locationTime).day(), businessHoursList);
            let result: ISystemAutoReply;

            if (currentDay && currentDay.isEnabled) {
                result = this.calculateBusinessHoursDelay(
                    this.convertBusinessHoursToRightFormat(currentDay.startTime, locationTime),
                    this.convertBusinessHoursToRightFormat(currentDay.endTime, locationTime),
                    locationTime,
                    isNextDayHoliday
                );
            } else {
                result = this.calculateBusinessHoursDelay(moment(locationTime).startOf('day'), moment(locationTime).startOf('day').add(1, 'days'), locationTime, isNextDayHoliday);
                result.status = OperationModeEnum.OutsideBusinessHours;
            }

            return result;
        }

        /**
         * Check if new message is a duplicate
         * @param {MeevoMessengerMessage[]} messageList
         * @param {Guid} newMessageId
         * @returns {boolean}
         */
        isMessageDuplicate(messageList: MeevoMessengerMessage[], newMessageId: Guid): boolean {
            return messageList.some((existingMessage: MeevoMessengerMessage) => existingMessage.id === newMessageId);
        }

        /**
         * Get splitted messages list related to the length limit
         * @param {string} msg
         * @param {number} msgLimit
         * @returns {Array<string>}
         */
        getSplittedMessagesList(msg: string, msgLimit: number = Constants.MESSAGE_LENGTH_LIMIT): Array<string> {
            const substrsCountAfterSplitNonSpaceString: number = 1;

            if (msg.length <= msgLimit) return [msg];

            // if there no spaces, message is split unconditionally
            if (msg.trim().split(' ').length === substrsCountAfterSplitNonSpaceString) return msg.match(new RegExp(`.{1,${msgLimit}}`, 'g'));

            // message splitting logic
            const splitedTextList = msg.trim().split(/([\s\n\r]+)/),
                messagesList = <string[]>[];
            let temp = '';

            for (const splitedText of splitedTextList) {
                const newPartialMessage: string = temp + splitedText;

                if (newPartialMessage.length <= msgLimit) {
                    temp = newPartialMessage;
                    continue;
                }

                messagesList.push(temp.trim());
                temp = splitedText;
            }

            // if last temp will be greater than 160 symbols
            if (temp) messagesList.push(...this.getSplittedMessagesList(temp.trim()));

            return messagesList;
        }

        /**
         * Get asynchronously attachments for the message and attach them to the message
         * @param {MeevoMessengerMessage | IMeevoMessengerNotification} messages
         * @param {Function} onAttachmentGet
         * @returns {MeevoMessengerMessage | IMeevoMessengerNotification}
         */
        getAttachmentsForMessage(msg: MeevoMessengerMessage, onAttachmentGet?: Function): MeevoMessengerMessage | IMeevoMessengerNotification {
            if (!msg.hasAttachment || (msg.attachments && msg.attachments.length)) return msg;

            msg.attachments = []; // create link for future attachments
            this.meevoMessengerApiService.getAttachmentsByExternalMsgId(msg.externalId, msg.classification).then((attachments: Meevo.Model.IMeevoMessengerAttachmentItem[]) => {
                attachments.length ? msg.attachments.push(...attachments) : msg.attachments.push({ unsupportedAttachment: true });

                if (onAttachmentGet) onAttachmentGet();
            });

            return msg;
        }

        /**
         * Get client info for the textel side nav from clients list
         * @param {Guid} clientId
         * @returns {ng.IPromise<Meevo.Model.Client>} promise
         */
        getClientInfoForSideNav(clientId: Guid): ng.IPromise<Meevo.Model.Client> {
            // This method is used to get a client with needed properties
            return this.meevoMessengerApiService.getClientById(clientId);
        }

        /**
         * Update conversation using other settings
         * @param {MeevoMessengerConversation} conversationInfo
         * @param {IRecipientRouteParams} recipientRoutParams
         * @param {Function} callback
         * @returns {any} stateParam
         */
        getStateParamsForClientRouting(conversation: MeevoMessengerConversation, recipientRoutParams: IRecipientRouteParams, callback?: Function, customClientId?: Guid) {
            const clientId = conversation && conversation.clientId ? conversation.clientId : customClientId
            const phoneNumber = conversation && !conversation.clientId ? conversation.phoneNumber : '';
            const stateParams: any = {
                actionState: recipientRoutParams.options.actionState,
                callback,
                callbackState: this.$state.current.name,
                targetState: Meevo.Model.MaintenanceFormVisualState.Detail,
                initialEditSection: recipientRoutParams.options.initialEditSection,
                initialEditSubSection: recipientRoutParams.options.initialEditSubSection,
                id: clientId,
                clientDisplayName: conversation && conversation.displayName ? `${conversation.displayName}` : null
            };

            // If there is no clientId, we create new client with this phoneNumber
            if (recipientRoutParams.type === RecipientRouteType.AddNewClientProfile && !clientId) {
                // textel work only with american phone number
                const americanCountryCode = 1;
                stateParams.newPhoneNumber = {
                    number: phoneNumber,
                    // countryCode: conversation.countryCode || americanCountryCode
                };
            }

            return stateParams;
        }

        /**
         * Find conversation from the list by properties
         * @param {Array<MeevoMessengerConversation>} conversationsList
         * @param {any} properties
         * @returns {MeevoMessengerConversation}
         */
        findConversation(conversationsList: Array<MeevoMessengerConversation>, properties?: any): MeevoMessengerConversation {
            if (angular.isUndefined(properties)) return;

            return _.findWhere(conversationsList, properties);
        }

        /**
         * Get business hours by day number
         * @param {moment.Moment} startTime
         * @param {moment.Moment} endTime
         * @param {string} locationTime
         * @param {boolean} isNextHoliday
         * @returns {ISystemAutoReply}
         */
        calculateBusinessHoursDelay(startTime: moment.Moment, endTime: moment.Moment, locationTime: string, isNextHoliday: boolean): ISystemAutoReply {
            let now = moment(locationTime);

            if (now.isBetween(startTime, endTime)) {
                return { status: OperationModeEnum.WorkingHours, delay: endTime.diff(now) };
            }

            let delay: number;
            let endDay = now.clone().startOf('day').add(1, 'day');

            if (now.isBetween(endTime, endDay)) {
                const nextDay: BusinessHours = this.getBusinessHoursForDay(moment(locationTime).add(1, 'days').day(), this.businessHoursList);
                delay = isNextHoliday || !nextDay.isEnabled ?
                    endDay.diff(now) : // Recheck at midnight
                    this.convertBusinessHoursToRightFormat(nextDay.startTime, moment(locationTime).add(1, 'days').format()).diff(now);
            } else { // It's a period now.isBetween(end day previouse, startTime)
                delay = startTime.diff(now);
            }

            return { status: OperationModeEnum.OutsideBusinessHours, delay };
        }

        /**
         * Get business hours by day number
         * @param {number} dayNumber
         * @param {BusinessHours[]} businessHoursList
         * @returns {BusinessHours}
         */
        getBusinessHoursForDay(dayNumber: number, businessHoursList: BusinessHours[]): BusinessHours {
            let dayOfWeek: WeekdayNameEnum;
            switch (dayNumber) {
                case 0:
                    dayOfWeek = WeekdayNameEnum.Sunday;
                    break;
                case 1:
                    dayOfWeek = WeekdayNameEnum.Monday;
                    break;
                case 2:
                    dayOfWeek = WeekdayNameEnum.Tuesday;
                    break;
                case 3:
                    dayOfWeek = WeekdayNameEnum.Wednesday;
                    break;
                case 4:
                    dayOfWeek = WeekdayNameEnum.Thursday;
                    break;
                case 5:
                    dayOfWeek = WeekdayNameEnum.Friday;
                    break;
                case 6:
                    dayOfWeek = WeekdayNameEnum.Saturday;
                    break;
            };

            return businessHoursList.find((businessHour: BusinessHours) => businessHour.weekdayName === dayOfWeek);;
        }

        /**
         * Convert business hours for current date (now it's have Jan 1 1900)
         * @param {string} date
         * @param {string} to
         * @returns {moment.Momen}
         */
        convertBusinessHoursToRightFormat(date: string, to: string): moment.Moment {
            const momentTo: moment.Moment = moment(to);
            return moment(date).year(momentTo.year()).month(momentTo.month()).date(momentTo.date());
        };

        /**
         * Filter Arrivals conversation by statuses
         * @param {boolean} checkInFilter
         * @param {boolean} checkOutFilter
         * @param { Array<MeevoMessengerConversation>} initialList
         * @returns {Array<MeevoMessengerConversation>}
         */
        filterConversationList(checkInFilter: boolean, checkOutFilter: boolean, initialList: Array<MeevoMessengerConversation>): Array<MeevoMessengerConversation> {
            let conversationList;
            if (checkInFilter && !checkOutFilter) {
                conversationList = this.getNewConversationsList(initialList, AppointmentStatuses.CheckedIn)
            } else if(checkOutFilter && !checkInFilter) {
                conversationList = this.getNewConversationsList(initialList, AppointmentStatuses.CheckedOut)
            } else if(checkOutFilter && checkInFilter){
                conversationList = this.getNewConversationsList(initialList, AppointmentStatuses.CheckedIn, AppointmentStatuses.CheckedOut)
            } else {
                conversationList = initialList;
            }
            return conversationList;
        }

        getNewConversationsList(initialList: Array<MeevoMessengerConversation>, appointStateOne: AppointmentStatuses, appointStateTwo: AppointmentStatuses = null): Array<MeevoMessengerConversation> {
            return initialList.filter((conversation: MeevoMessengerConversation) => {
                // if (conversation.appointState == appointStateOne || (appointStateTwo != null && conversation.appointState == appointStateTwo)) return conversation;
                return conversation;
            });
        }

        /**
         * Get default date filter params
         * @returns {IDateFilter[]}
         */
        get messengerDateFilter(): IDateFilter[] {
            return [
                { name: this.$translate.instant('LABEL_TODAY'), status: PresetStatus.Today },
                { name: this.$translate.instant('LABEL_YESTERDAY'), status: PresetStatus.Yesterday },
                { name: this.$translate.instant('LABEL_THIS_WEEK'), status: PresetStatus.ThisWeek },
                { name: this.$translate.instant('LABEL_LAST_WEEK'), status: PresetStatus.LastWeek },
                { name: this.$translate.instant('LABEL_LAST_SEVEN_DAYS'), status: PresetStatus.LastSevenDays },
                { name: this.$translate.instant('LABEL_PREVIOUS_TWO_WEEKS'), status: PresetStatus.PreviousTwoWeeks },
                { name: this.$translate.instant('LABEL_THIS_MONTH'), status: PresetStatus.ThisMonth },
                { name: this.$translate.instant('LABEL_LAST_MONTH'), status: PresetStatus.LastMonth },
                { name: this.$translate.instant('LABEL_THIS_YEAR'), status: PresetStatus.ThisYear },
                { name: this.$translate.instant('LABEL_LAST_YEAR'), status: PresetStatus.LastYear }
            ];
        }

        /**
         * Get default messages filter params
         * @returns {MessageFilter[]}
         */
        get messengerMessageFilters(): MessageFilter[] {
            return [
                { name: this.$translate.instant('LABEL_ALL_MESSAGES'), type: MessageFilterOption.All },
                { name: this.$translate.instant('LABEL_NEW_MESSAGES'), type: MessageFilterOption.Unread },
                { name: this.$translate.instant('LABEL_MESSAGES_FOR_FOLLOW_UP'), type: MessageFilterOption.Marked }
            ];
        }
    }

    angular.module('meevo.plugins.meevoMessenger')
        .service('meevo.plugins.meevoMessenger.meevoMessengerService', MeevoMessengerService);
}
